import React, { useCallback } from 'react';
import { Box } from '@amzn/awsui-components-console';
import { uploadFileToS3 } from '../../../../utils/s3Upload';
import { ChatMessage, UploadedFile } from '../types';

interface UseChatHandlersProps {
    wsUiState: any;
    setWsUiState: React.Dispatch<React.SetStateAction<any>>;
    localMessages: ChatMessage[];
    setLocalMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
    allQuestions: any[];
    currentQuestionIndex: number;
    handleQuestionAnswer: (questionId: string, answer: string) => void;
    sendWebSocketControlMessage: (action: string, data: any) => void;
    generateTemplate: (feedback?: string) => Promise<void>;
    retryTemplateGeneration: () => Promise<void>;
    createJob: (jobName: string) => Promise<void>;
    handleTemplateDownload: () => Promise<void>;
    resetChat: (clearDraftJob?: boolean) => void;
    createNewDraftJob: () => Promise<string>;
    clearMessages: () => void;
    startTemplateStatusPolling: (jobId: string) => Promise<void>;
    onPreviewFile?: (file: UploadedFile) => void;
    userName?: string;
    setIsFullscreen?: React.Dispatch<React.SetStateAction<boolean>>;
}

// Helper function to generate personalized greeting content
const getGreetingContent = (userName?: string) => `👋 **Hello${userName && userName !== 'User' ? `, ${userName}` : ''}!** Welcome to ADDAPT AI Assistant.

I'm here to help you create custom data transformation templates. Here's what I can do:

🔧 **Template Generation** - Create dataset templates from your specifications
📝 **Q&A Flow** - Answer a few questions and I'll generate a template tailored to your needs
📂 **Excel Upload** - Upload an Excel file with your data mappings for automatic template creation
💬 **Feedback & Iteration** - Refine templates based on your feedback
👁️ **Preview & Download** - View and download your generated templates

**How would you like to get started?**`;

/**
 * Hook for message and interaction handlers
 */
export const useChatHandlers = ({
    wsUiState,
    setWsUiState,
    localMessages,
    setLocalMessages,
    allQuestions,
    currentQuestionIndex,
    handleQuestionAnswer,
    sendWebSocketControlMessage,
    generateTemplate,
    retryTemplateGeneration,
    createJob,
    handleTemplateDownload,
    resetChat,
    createNewDraftJob,
    clearMessages,
    startTemplateStatusPolling,
    onPreviewFile,
    userName,
    setIsFullscreen
}: UseChatHandlersProps) => {
    
    // Handle file upload and processing
    const handleFileUploadAndProcess = useCallback(async (file: File) => {
        try {
            // Show uploading status with loading bar
            const uploadingMessage: ChatMessage = {
                id: `uploading-status-${Date.now()}`,
                type: 'assistant',
                content: 'Uploading file...',
                timestamp: new Date(),
                status: 'sent',
                avatarLoading: true
            };
            setLocalMessages((prev: ChatMessage[]) => [...prev, uploadingMessage]);
            
            // Upload to S3 and get the file key
            const s3Key = await uploadFileToS3(file);
            
            // Store file data for template generation (merge with existing data to preserve jobId)
            window.__ADDAPT_TEMPLATE__ = {
                ...window.__ADDAPT_TEMPLATE__, // Preserve existing data (especially jobId!)
                fileData: {
                    s3Key,
                    fileName: file.name,
                    fileSize: file.size
                }
            };
            
            // Remove uploading status message
            setLocalMessages((prev: ChatMessage[]) => 
                prev.filter(msg => !msg.id.startsWith('uploading-status-'))
            );
            
            // Hide file upload button after upload
            setWsUiState((prev: any) => ({
                ...prev,
                show_file_upload: false,
                input_enabled: false,
                input_placeholder: 'Processing your file...'
            }));
            
            const successMessage: ChatMessage = {
                id: `success-${Date.now()}`,
                type: 'assistant',
                content: '✅ File uploaded successfully! Now generating template...',
                timestamp: new Date(),
                status: 'sent'
            };
            setLocalMessages((prev: ChatMessage[]) => [...prev, successMessage]);
            
            // Trigger template generation via API with the S3 key
            await generateTemplate('');
        } catch (error: any) {
            console.error('File upload failed:', error);
            
            // Remove uploading status message
            setLocalMessages((prev: ChatMessage[]) => 
                prev.filter(msg => !msg.id.startsWith('uploading-status-'))
            );
            
            const errorMessage: ChatMessage = {
                id: `upload-error-${Date.now()}`,
                type: 'system',
                content: `❌ Failed to upload file: ${error.message || 'Unknown error'}`,
                timestamp: new Date(),
                status: 'sent'
            };
            setLocalMessages((prev: ChatMessage[]) => [...prev, errorMessage]);
            
            // Re-enable file upload
            setWsUiState((prev: any) => ({
                ...prev,
                show_file_upload: true,
                input_enabled: true,
                input_placeholder: 'Please try uploading again'
            }));
        }
    }, [setLocalMessages, setWsUiState, generateTemplate]);

    // Handle support prompt click
    const handleSupportPromptClick = useCallback(async ({ detail }: any) => {
        // Handle create job with template
        if (detail.id === 'create-job-with-template') {
            // Update the template options message to remove support prompts
            setLocalMessages((prev: ChatMessage[]) => 
                prev.map(msg => {
                    if (msg.id.startsWith('template-options-')) {
                        return { ...msg, supportPrompts: undefined };
                    }
                    return msg;
                })
            );
            
            // Show job name prompt
            setLocalMessages((prev: ChatMessage[]) => [...prev, {
                id: `job-name-prompt-${Date.now()}`,
                type: 'assistant',
                content: `🎉 **Great! Let's create a job with this template.**\n\n**Please provide a name for your job:**`,
                timestamp: new Date(),
                status: 'sent'
            }]);
            
            // Enable input for job name
            setWsUiState((prev: any) => ({
                ...prev,
                input_enabled: true,
                input_placeholder: 'Enter a name for your job...',
                awaiting_job_name: true,
                awaiting_feedback: false
            }));
            return;
        }
        
        // Handle provide feedback
        if (detail.id === 'provide-feedback') {
            // Update the template options message to remove support prompts
            setLocalMessages((prev: ChatMessage[]) => 
                prev.map(msg => {
                    if (msg.id.startsWith('template-options-')) {
                        return { ...msg, supportPrompts: undefined };
                    }
                    return msg;
                })
            );
            
            // Show feedback prompt
            setLocalMessages((prev: ChatMessage[]) => [...prev, {
                id: `feedback-prompt-${Date.now()}`,
                type: 'assistant',
                content: `💬 **Please provide your feedback to improve the template:**\n\nDescribe what changes you'd like to see or what needs to be corrected.`,
                timestamp: new Date(),
                status: 'sent'
            }]);
            
            // Enable input for feedback
            setWsUiState((prev: any) => ({
                ...prev,
                input_enabled: true,
                input_placeholder: 'Enter your feedback...',
                awaiting_feedback: true,
                awaiting_job_name: false
            }));
            return;
        }
        
        // Handle provide feedback for generation failure
        if (detail.id === 'provide-feedback-generation') {
            // Update the generation failed message to remove support prompts
            setLocalMessages((prev: ChatMessage[]) => 
                prev.map(msg => {
                    if (msg.id.startsWith('generation-failed-')) {
                        return { ...msg, supportPrompts: undefined };
                    }
                    return msg;
                })
            );
            
            // Show feedback prompt
            setLocalMessages((prev: ChatMessage[]) => [...prev, {
                id: `feedback-prompt-${Date.now()}`,
                type: 'assistant',
                content: `💬 **Please provide feedback to help fix the generation issue:**\n\nDescribe what you think went wrong or provide additional details about your requirements.`,
                timestamp: new Date(),
                status: 'sent'
            }]);
            
            // Enable input for feedback
            setWsUiState((prev: any) => ({
                ...prev,
                input_enabled: true,
                input_placeholder: 'Enter your feedback to retry generation...',
                awaiting_feedback: true,
                awaiting_job_name: false
            }));
            return;
        }
        
        // Handle retry generation
        if (detail.id === 'retry-generation') {
            // Update the generation failed message to remove support prompts
            setLocalMessages((prev: ChatMessage[]) => 
                prev.map(msg => {
                    if (msg.id.startsWith('generation-failed-')) {
                        return { ...msg, supportPrompts: undefined };
                    }
                    return msg;
                })
            );
            
            // Retry using the stored request payload (includes questionAnswers or excelSheetUri)
            retryTemplateGeneration();
            return;
        }
        
        // Handle start over
        if (detail.id === 'start-over') {
            // Update the generation failed message to remove support prompts
            setLocalMessages((prev: ChatMessage[]) => 
                prev.map(msg => {
                    if (msg.id.startsWith('generation-failed-')) {
                        return { ...msg, supportPrompts: undefined };
                    }
                    return msg;
                })
            );
            
            // Full reset
            clearMessages();
            setLocalMessages([]);
            resetChat(true);
            
            // Create new draft job
            createNewDraftJob().then(() => {
                setLocalMessages([{
                    id: `reset-success-${Date.now()}`,
                    type: 'assistant',
                    content: getGreetingContent(userName),
                    timestamp: new Date(),
                    status: 'sent'
                }]);
            }).catch(error => {
                console.error('Start over failed:', error);
            });
            return;
        }
        
        // Handle re-upload file
        if (detail.id === 'reupload-file') {
            // Update the template options message to remove support prompts
            setLocalMessages((prev: ChatMessage[]) => 
                prev.map(msg => {
                    if (msg.id.startsWith('template-options-')) {
                        return { ...msg, supportPrompts: undefined };
                    }
                    return msg;
                })
            );
            
            // Clear any old file-related messages (file selection, upload confirmations, etc.)
            setLocalMessages((prev: ChatMessage[]) => 
                prev.filter(msg => {
                    // Check by ID
                    if (msg.id.startsWith('file-selected-') || 
                        msg.id.startsWith('change-file-') ||
                        msg.id.startsWith('uploading-')) {
                        return false;
                    }
                    // Check by content (ensure it's a string)
                    const contentStr = typeof msg.content === 'string' ? msg.content : '';
                    if (contentStr.includes('Please select a different Excel file') ||
                        contentStr.includes('Please select an Excel file')) {
                        return false;
                    }
                    return true;
                })
            );
            
            // Clear template data (except jobId)
            if (window.__ADDAPT_TEMPLATE__) {
                const preservedJobId = window.__ADDAPT_TEMPLATE__.jobId;
                const preservedCreatedAt = window.__ADDAPT_TEMPLATE__.createdAt;
                
                // Clear template data
                delete window.__ADDAPT_TEMPLATE__;
                
                // Restore jobId and createdAt
                if (preservedJobId) {
                    window.__ADDAPT_TEMPLATE__ = {
                        jobId: preservedJobId,
                        createdAt: preservedCreatedAt
                    };
                }
            }
            
            // Show file upload prompt
            setLocalMessages((prev: ChatMessage[]) => [...prev, {
                id: `reupload-prompt-${Date.now()}`,
                type: 'assistant',
                content: `📁 **Please select a new Excel file to generate a template:**\n\nUse the file button below to select your Excel file (.xlsx or .xls).`,
                timestamp: new Date(),
                status: 'sent'
            }]);
            
            // Enable file upload
            setWsUiState((prev: any) => ({
                ...prev,
                show_file_upload: true,
                input_enabled: true,
                input_placeholder: 'Select an Excel file and click send to upload',
                awaiting_feedback: false,
                awaiting_job_name: false
            }));
            return;
        }
        
        // Handle file upload confirmation
        if (detail.id === 'upload-file') {
            const pendingFile = window.__ADDAPT_PENDING_FILE__;
            if (pendingFile) {
                // Remove the file selection confirmation message (with buttons)
                setLocalMessages((prev: ChatMessage[]) => 
                    prev.filter(msg => !msg.id.startsWith('file-selected-'))
                );
                
                // User confirmed upload, proceed
                setLocalMessages((prev: ChatMessage[]) => [...prev, {
                    id: `uploading-${Date.now()}`,
                    type: 'user',
                    content: `✅ Uploading ${pendingFile.name}...`,
                    timestamp: new Date(),
                    status: 'sending'
                }]);
                
                // Store for preview option
                window.__ADDAPT_UPLOADED_FILE__ = pendingFile;
                
                // Start upload process
                handleFileUploadAndProcess(pendingFile);
                
                // Clean up
                delete window.__ADDAPT_PENDING_FILE__;
            }
            return;
        }
        
        // Handle change file request
        if (detail.id === 'change-file') {
            // Remove the previous file selection confirmation message AND previous change-file messages
            setLocalMessages((prev: ChatMessage[]) => 
                prev.filter(msg => 
                    !msg.id.startsWith('file-selected-') && 
                    !msg.id.startsWith('change-file-')
                )
            );
            
            // Re-enable file input for new selection
            setWsUiState((prev: any) => ({
                ...prev,
                show_file_upload: true,
                input_enabled: true,
                input_placeholder: 'Select a new Excel file and click send to upload'
            }));
            
            // Add single change-file message
            setLocalMessages((prev: ChatMessage[]) => [...prev, {
                id: `change-file-${Date.now()}`,
                type: 'assistant',
                content: '🔄 Please select a different Excel file using the file button.',
                timestamp: new Date(),
                status: 'sent'
            }]);
            
            // Clean up pending file
            delete window.__ADDAPT_PENDING_FILE__;
            return;
        }
        
        // Check if it's a preview file action
        if (detail.id === 'preview-file') {
            // Exit fullscreen mode before showing preview modal
            if (setIsFullscreen) {
                setIsFullscreen(false);
            }
            // Don't remove support prompts - keep them visible so user can still upload or change file after preview
            // Check both pending and uploaded files
            const fileToPreview = window.__ADDAPT_PENDING_FILE__ || window.__ADDAPT_UPLOADED_FILE__;
            if (fileToPreview && onPreviewFile) {
                const fileRef: UploadedFile = {
                    id: `preview-${Date.now()}`,
                    name: fileToPreview.name,
                    size: fileToPreview.size,
                    type: fileToPreview.type || 'application/octet-stream',
                    lastModified: fileToPreview.lastModified,
                    originalFile: fileToPreview
                };
                onPreviewFile(fileRef);
            }
            return;
        }
        
        // Handle view template action
        if (detail.id === 'view-template') {
            const templateUri = window.__ADDAPT_TEMPLATE__?.templateUri;
            if (templateUri) {
                // This will be called from the main component
                // handleTemplateView();
            }
            return;
        }
        
        // Handle download template action
        if (detail.id === 'download-template' || detail.action === 'download_template') {
            handleTemplateDownload();
            return;
        }
        
        // Handle chips from WebSocket control mode
        const chip = wsUiState.chips.find((c: any) => c.id === detail.id);
        if (chip) {
            
            // Handle question answers from chips (new sequential flow) - don't add user message here
            if (chip.action === 'answer_question' && chip.value) {
                if (allQuestions.length > 0 && currentQuestionIndex < allQuestions.length) {
                    const currentQuestion = allQuestions[currentQuestionIndex];
                    handleQuestionAnswer(currentQuestion.id, chip.value);
                    return;
                }
            }
            
            // For other actions, add the user message
            const userMessage: ChatMessage = {
                id: `user-${Date.now()}`,
                type: 'user',
                content: chip.text,
                timestamp: new Date(),
                status: 'sending'
            };
            setLocalMessages((prev: ChatMessage[]) => [...prev, userMessage]);
            
            const lastQuestion = [...localMessages].reverse().find((m: ChatMessage) => 
                m.type === 'assistant' && m.id.startsWith('question-')
            );
            const questionId = lastQuestion?.id.replace('question-', '');

            
            switch (chip.action) {
                case 'start_questionnaire':
                    // Show loading state
                    const loadingMsg: ChatMessage = {
                        id: `loading-${Date.now()}`,
                        type: 'assistant',
                        content: <Box color="text-status-inactive">Generating a response</Box>,
                        timestamp: new Date(),
                        avatarLoading: true,
                        status: 'sent'
                    };
                    setLocalMessages((prev: ChatMessage[]) => [...prev, loadingMsg]);
                    
                    // Send without sessionId if it's null/undefined to avoid backend issues
                    const wsSessionId = (window as any).__WS_SESSION_ID__;
                    const questionnaireData = wsSessionId ? { sessionId: wsSessionId } : {};
                    sendWebSocketControlMessage('start_questionnaire', questionnaireData);
                    break;
                    
                case 'upload_excel':
                    setWsUiState((prev: any) => ({
                        ...prev,
                        show_file_upload: true,
                        input_enabled: true,
                        input_placeholder: 'Select an Excel file and click send to upload',
                        chips: [] // Clear chips when entering upload mode
                    }));
                    
                    // No need to notify WebSocket about upload mode since file upload is handled via API
                    const instructionMessage: ChatMessage = {
                        id: `instruction-${Date.now()}`,
                        type: 'assistant',
                        content: 'Please select an Excel file (.xlsx, .xls) using the file button, then click the send button to upload.',
                        timestamp: new Date(),
                        status: 'sent'
                    };
                    setLocalMessages((prev: ChatMessage[]) => [...prev, instructionMessage]);
                    break;
                    
                case 'select_option':
                case 'skip_question':
                case 'previous_question':
                case 'preview_template':
                case 'regenerate':
                    // Show loading state for these actions
                    const actionLoadingMsg: ChatMessage = {
                        id: `loading-${Date.now()}`,
                        type: 'assistant',
                        content: <Box color="text-status-inactive">Generating a response</Box>,
                        timestamp: new Date(),
                        avatarLoading: true,
                        status: 'sent'
                    };
                    setLocalMessages((prev: ChatMessage[]) => [...prev, actionLoadingMsg]);
                    
                    sendWebSocketControlMessage('chip_selected', {
                        chip_action: chip.action,
                        chip_value: chip.value || chip.text,
                        question_id: questionId,
                        chip_id: chip.id
                    });
                    break;
                    
                case 'download_template':
                    handleTemplateDownload();
                    break;
                    
                case 'generate_template':
                    generateTemplate();
                    break;
                    
                case 'retry_generation':
                    // Retry template generation
                    generateTemplate();
                    break;
                    
                case 'retry_with_feedback':
                    // Enable input for feedback
                    setWsUiState((prev: any) => ({
                        ...prev,
                        input_enabled: true,
                        input_placeholder: 'Describe how to fix the validation errors...',
                        awaiting_feedback: true,
                        chips: []
                    }));
                    break;
                    
                case 'check_status':
                    // Check status again
                    if (window.__ADDAPT_TEMPLATE__?.jobId) {
                        startTemplateStatusPolling(window.__ADDAPT_TEMPLATE__.jobId);
                    }
                    break;
                    
                case 'reset_chat':
                    // Full reset: clear messages, reset chat, and create new draft job
                    clearMessages();
                    setLocalMessages([]);
                    resetChat(true); // Clear draft job - new one will be created
                    
                    // Create new draft job with fresh TTL
                    try {
                        await createNewDraftJob();
                        
                        // Show success message
                        setLocalMessages([{
                            id: `reset-success-${Date.now()}`,
                            type: 'assistant',
                            content: getGreetingContent(userName),
                            timestamp: new Date(),
                            status: 'sent'
                        }]);
                    } catch (error) {
                        console.error('Reset failed:', error);
                    }
                    break;
                    
                case 'provide_feedback':
                    // Enable input for feedback
                    setWsUiState((prev: any) => ({
                        ...prev,
                        input_enabled: true,
                        input_placeholder: 'Enter your feedback to fix validation errors',
                        chips: []
                    }));
                    break;
                    
                case 'submit_feedback':
                    // Send feedback to backend
                    const templateId = window.__ADDAPT_TEMPLATE__?.templateId;
                    if (templateId) {
                        sendWebSocketControlMessage('submit_feedback', {
                            templateId,
                            feedback: 'Please fix the validation errors',
                            jobId: window.__ADDAPT_TEMPLATE__?.jobId
                        });
                    }
                    break;
                    
                case 'start_over':
                    // Full reset: clear messages, reset chat, and create new draft job
                    clearMessages();
                    setLocalMessages([]);
                    resetChat(true); // Clear draft job - new one will be created
                    
                    // Create new draft job with fresh TTL
                    try {
                        await createNewDraftJob();
                        
                        // Show success message
                        setLocalMessages([{
                            id: `reset-success-${Date.now()}`,
                            type: 'assistant',
                            content: getGreetingContent(userName),
                            timestamp: new Date(),
                            status: 'sent'
                        }]);
                    } catch (error) {
                        console.error('Start over failed:', error);
                    }
                    break;
                    
                case 'navigate':
                    // Navigate to specified route
                    if (chip.value) {
                        window.location.href = chip.value;
                    }
                    break;
                    
                default:
                    sendWebSocketControlMessage('chip_selected', {
                        chip_id: chip.id,
                        chip_action: chip.action,
                        chip_value: chip.value
                    });
            }
        } else {
            console.warn('Chip not found in wsUiState.chips:', detail.id, wsUiState.chips);
        }
    }, [
        wsUiState, 
        localMessages, 
        allQuestions, 
        currentQuestionIndex, 
        handleQuestionAnswer, 
        sendWebSocketControlMessage, 
        generateTemplate, 
        handleTemplateDownload, 
        resetChat, 
        createNewDraftJob,
        clearMessages,
        startTemplateStatusPolling, 
        onPreviewFile,
        setLocalMessages,
        setWsUiState
    ]);

    // Handle copy message
    const handleCopyMessage = useCallback((content: string) => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(content).catch(error => 
                console.error('Failed to copy:', error)
            );
        }
    }, []);

    return {
        handleFileUploadAndProcess,
        handleSupportPromptClick,
        handleCopyMessage
    };
};

